import { Link } from "react-router-dom";
import { Trash2, ArrowUpRight, ClipboardList } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useStore } from "@/context/StoreContext";

const QTY_STEPS = [50, 100, 250, 500, 1000];

export default function GiftingList() {
  const { list, removeFromList, setQty } = useStore();

  return (
    <div data-testid="gifting-list-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 px-4 sm:px-8 lg:px-16 pb-24">
      <Reveal><Eyebrow>Your Shortlist</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">My Gifting List</h1>
      </Reveal>

      {list.length === 0 ? (
        <Reveal delay={0.15}>
          <div data-testid="gifting-list-empty" className="mt-16 border border-white/10 p-12 text-center">
            <ClipboardList size={28} className="mx-auto text-champagne mb-6" strokeWidth={1.25} />
            <p className="font-serif text-2xl mb-3">Your list is empty.</p>
            <p className="text-ivory/45 text-sm mb-8">Add products as you browse, then request one corporate quote for everything.</p>
            <Link to="/collections" data-testid="gifting-list-browse" className="inline-flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:bg-champagne-light transition-colors">
              Browse Collections <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
      ) : (
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 divide-y divide-white/10 border-t border-b border-white/10">
            {list.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.05}>
                <div data-testid={`gifting-item-${item.id}`} className="py-6 flex gap-5 sm:gap-8 items-center">
                  <Link to={`/product/${item.id}`} className="w-20 h-24 sm:w-24 sm:h-28 shrink-0 overflow-hidden bg-charcoal-soft">
                    <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover opacity-90" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne">{item.brand}</p>
                    <Link to={`/product/${item.id}`} className="font-serif text-lg sm:text-xl hover:text-champagne-light transition-colors block truncate">{item.name}</Link>
                    <div className="flex items-center gap-3 mt-3">
                      <label htmlFor={`qty-${item.id}`} className="text-[10px] font-mono uppercase tracking-[0.2em] text-ivory/40">Units</label>
                      <input
                        id={`qty-${item.id}`}
                        data-testid={`gifting-qty-${item.id}`}
                        type="number"
                        min={1}
                        value={item.qty}
                        onChange={(e) => setQty(item.id, Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 bg-transparent border border-white/15 focus:border-champagne text-ivory text-sm px-3 py-2 outline-none"
                      />
                      <div className="hidden sm:flex gap-1.5">
                        {QTY_STEPS.map((s) => (
                          <button
                            key={s}
                            data-testid={`gifting-qty-preset-${item.id}-${s}`}
                            onClick={() => setQty(item.id, s)}
                            className={`text-[10px] font-mono px-2 py-1 border transition-colors ${item.qty === s ? "border-champagne text-champagne" : "border-white/10 text-ivory/40 hover:text-ivory"}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={`gifting-remove-${item.id}`}
                    onClick={() => removeFromList(item.id)}
                    aria-label={`Remove ${item.name}`}
                    className="text-ivory/35 hover:text-red-400 transition-colors p-2"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1} className="border border-white/10 bg-[#161616] p-8 lg:sticky lg:top-28">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne mb-6">Summary</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-ivory/60">
                  <span>Products</span><span data-testid="gifting-summary-count" className="text-ivory">{list.length}</span>
                </div>
                <div className="flex justify-between text-ivory/60">
                  <span>Total units</span>
                  <span data-testid="gifting-summary-units" className="text-ivory">{list.reduce((a, b) => a + (b.qty || 0), 0)}</span>
                </div>
                <div className="flex justify-between text-ivory/60">
                  <span>Pricing</span><span className="text-ivory">On Request</span>
                </div>
              </div>
              <Link
                to="/request-quote"
                data-testid="gifting-request-quote"
                className="mt-8 flex items-center justify-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 hover:bg-champagne-light transition-colors duration-300"
              >
                Request Quote for Selected Products <ArrowUpRight size={14} />
              </Link>
              <p className="text-ivory/30 text-xs mt-5 leading-relaxed">
                Your selected products and quantities are carried into the enquiry form automatically.
              </p>
            </Reveal>
          </div>
        </div>
      )}
    </div>
  );
}
